import React, { useState, useEffect, useRef } from 'react';
import { parseSRT, getCurrentSubtitle } from '../../../utils/srtParser';
import Modal from 'react-bootstrap/Modal';
import './style.scss';

const INFO_SPOT_AUDIO_OPEN_EVENT = 'infoSpotAudioOpen';

const InfoSpotAudioModal = ({ show, onClose, title, audioSrc, srtSrc }) => {
  const audioRef = useRef(null);
  const transcriptRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [subtitles, setSubtitles] = useState([]);
  const [currentSubtitle, setCurrentSubtitle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Pause tour audio when this modal opens; stop when modal closes
  useEffect(() => {
    if (show) {
      window.dispatchEvent(new CustomEvent(INFO_SPOT_AUDIO_OPEN_EVENT));
    }
  }, [show]);

  useEffect(() => {
    if (!show && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  }, [show]);

  // When opening a different info spot (new audioSrc), reset state and load new source so the second track can play
  useEffect(() => {
    if (show && audioSrc) {
      setCurrentTime(0);
      setDuration(0);
      setIsPlaying(false);
      setCurrentSubtitle(null);
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
        audio.load();
      }
    }
  }, [show, audioSrc]);

  useEffect(() => {
    if (!show || !audioSrc || !audioRef.current) return;

    const audio = audioRef.current;

    const tryAutoPlay = () => {
      if (audio.readyState < 2) return;

      const playPromise = audio.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise
          .then(() => setIsPlaying(true))
          .catch((error) => {
            console.error('Info spot auto-play failed:', error);
          });
      }
    };

    audio.addEventListener('canplay', tryAutoPlay);
    tryAutoPlay();

    return () => {
      audio.removeEventListener('canplay', tryAutoPlay);
    };
  }, [show, audioSrc]);

  // Load SRT file
  useEffect(() => {
    if (srtSrc && show) {
      setIsLoading(true);
      fetch(srtSrc)
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((text) => {
          setSubtitles(parseSRT(text));
          setIsLoading(false);
        })
        .catch(() => {
          setSubtitles([]);
          setIsLoading(false);
        });
    }
  }, [srtSrc, show]);

  // Update current subtitle and scroll active into view
  useEffect(() => {
    if (!show || !audioRef.current) return;
    const interval = setInterval(() => {
      const time = audioRef.current.currentTime;
      setCurrentTime(time);
      const sub = getCurrentSubtitle(subtitles, time);
      setCurrentSubtitle(sub);
    }, 100);
    return () => clearInterval(interval);
  }, [show, subtitles]);

  useEffect(() => {
    if (currentSubtitle && transcriptRef.current) {
      const active = transcriptRef.current.querySelector('.transcript-cue--active');
      if (active) active.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [currentSubtitle]);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onMeta = () => {
      if (audio.duration && isFinite(audio.duration)) setDuration(audio.duration);
    };
    const onTime = () => setCurrentTime(audio.currentTime);
    const onEnded = () => setIsPlaying(false);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('durationchange', onMeta);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    return () => {
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('durationchange', onMeta);
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
    };
  }, [show]);

  const togglePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => {});
    }
  };

  const handleSeek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    audioRef.current.currentTime = percent * duration;
  };

  const formatTime = (t) => {
    if (!isFinite(t)) return '0:00';
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const fullTranscript = subtitles.map((s) => s.text).join(' ').trim();

  if (!show) return null;

  return (
    <Modal
      show={show}
      onHide={onClose}
      dialogClassName="info-spot-audio-modal"
      backdrop
      keyboard
      centered
    >
      <Modal.Body className="info-spot-audio-modal-body">
        <div className="info-spot-audio-header">
          <div className="info-spot-audio-accent" aria-hidden="true" />
          <button
            type="button"
            className="info-spot-audio-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
          <h2 className="info-spot-audio-title">{title}</h2>
        </div>

        <div className="info-spot-audio-transcript-wrap">
          <div className="info-spot-audio-transcript-box" ref={transcriptRef}>
            {isLoading ? (
              <p className="info-spot-audio-loading">Loading transcript…</p>
            ) : subtitles.length > 0 ? (
              <div className="info-spot-audio-transcript-cues">
                {subtitles.map((cue) => (
                  <p
                    key={cue.id}
                    className={`transcript-cue ${currentSubtitle && currentSubtitle.id === cue.id ? 'transcript-cue--active' : ''}`}
                  >
                    {cue.text}
                  </p>
                ))}
              </div>
            ) : (
              <p className="info-spot-audio-empty">{fullTranscript || 'No transcript available.'}</p>
            )}
          </div>
        </div>

        {audioSrc && (
          <div className="info-spot-audio-player-wrap">
            <audio ref={audioRef} key={audioSrc} src={audioSrc} preload="auto" autoPlay />
            <div className="info-spot-audio-controls">
              <button
                type="button"
                className={`info-spot-audio-play ${isPlaying ? 'playing' : ''}`}
                onClick={togglePlayPause}
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="4" width="4" height="16" rx="1" />
                    <rect x="14" y="4" width="4" height="16" rx="1" />
                  </svg>
                ) : (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 4.5V19.5L19 12L7 4.5Z" />
                  </svg>
                )}
              </button>
              <div className="info-spot-audio-progress-block">
                <div className="info-spot-audio-progress-wrap" onClick={handleSeek}>
                  <div
                    className="info-spot-audio-progress-fill"
                    style={{
                      width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%',
                    }}
                  />
                </div>
                <span className="info-spot-audio-time">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default InfoSpotAudioModal;
export { INFO_SPOT_AUDIO_OPEN_EVENT };
